import { useEffect, useId, useRef, useState } from 'react';
import {
    dueInfo, isOpenTask, isTypingTarget, toDateInputValue, toDueDatePayload, TYPE_LABELS, typeKey,
} from '../utils/taskBoard';
import { CONFIRM_GUARD_MS } from '../components/shared/CancelTaskConfirm';

const ARROWS = ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'];

// Real overlays that own the keyboard: the header's user menu or a modal.
const OPEN_OVERLAY_SELECTOR = '.user-dropdown, [aria-modal="true"]';
// Places where focus means "the user is in a menu or dialog right now".
const FOCUS_IN_OVERLAY_SELECTOR = '.task-card-menu, [data-task-menu], .user-dropdown, [role="dialog"], [aria-modal="true"]';

export const MISSING_UNIT_TITLE = 'This unit is not in the current inventory (sold or removed), so there is no unit page to open.';

/**
 * Everything one Task Board card does, shared by today's card
 * (TaskBoard/TaskCard) and the redesigned one (TasksV2/TaskCardV2) so the two
 * cannot drift: the More menu (focus on its first item, Escape and outside
 * clicks close it, tabbing out closes it), the cancel / delete confirms, the
 * inline edit (title, due date, details), the quick status buttons, the
 * keyboard (Enter, arrows, S, C with today's guards) and the click guards.
 *
 * unitStatus: 'found' (unit joined), 'missing' (inventory loaded, unit not in
 * it — nothing to open), or 'unknown' (inventory not loaded / not available).
 *
 * `onActivate` is what a plain click does (today: open the unit; v2: open the
 * preview, D10); `onOpenUnit` is what Enter does (open the unit, both).
 */
export default function useTaskCard({
    task, unitStatus, canEdit, onStatusChange, onQuickStatus, onNavigate, onOpenUnit, onActivate,
    onDelete, onEdit,
}) {
    const [menuOpen, setMenuOpen] = useState(false);
    const [confirming, setConfirming] = useState(null); // null | 'cancel' | 'delete'
    const [editing, setEditing] = useState(false);
    const [editTitle, setEditTitle] = useState(task.title);
    const [editDetails, setEditDetails] = useState(task.details || '');
    const [editDueDate, setEditDueDate] = useState(toDateInputValue(task.due_date));
    const moreBtnRef = useRef(null);
    const menuRef = useRef(null);
    const titleInputRef = useRef(null);
    const refocusMore = useRef(false);
    // When a menu action last fired: the tail of a double-click on it must not
    // land on the card body and open the unit.
    const lastMenuActionAt = useRef(0);
    const menuId = useId();

    const typeLabel = TYPE_LABELS[task.type] || TYPE_LABELS.other;
    const type = typeKey(task.type);
    const open = isOpenTask(task);
    const due = dueInfo(task);
    const canOpen = unitStatus !== 'missing';

    // Focus follows the menu: first item on open, back to "More actions" after
    // Escape, "Keep task" or leaving the edit form.
    useEffect(() => {
        if (menuOpen) menuRef.current?.querySelector('button')?.focus();
    }, [menuOpen]);
    useEffect(() => {
        if (!menuOpen && !confirming && !editing && refocusMore.current) {
            refocusMore.current = false;
            moreBtnRef.current?.focus();
        }
    }, [menuOpen, confirming, editing]);
    useEffect(() => {
        if (editing) titleInputRef.current?.focus();
    }, [editing]);

    // Close the menu on a click anywhere else.
    useEffect(() => {
        if (!menuOpen) return undefined;
        const onDown = (e) => {
            if (!menuRef.current?.contains(e.target) && !moreBtnRef.current?.contains(e.target)) setMenuOpen(false);
        };
        document.addEventListener('mousedown', onDown);
        return () => document.removeEventListener('mousedown', onDown);
    }, [menuOpen]);

    const closeMenu = (refocus) => {
        refocusMore.current = refocus;
        setMenuOpen(false);
    };

    const handleMenuKeyDown = (e) => {
        if (e.key === 'Escape') {
            e.stopPropagation();
            closeMenu(true);
        }
    };

    // Tabbing (or clicking) out of the menu closes it, so a forgotten menu
    // never lingers on one card while the user works on another.
    const handleQuickBlur = (e) => {
        if (!menuOpen) return;
        if (e.relatedTarget && e.currentTarget.contains(e.relatedTarget)) return;
        setMenuOpen(false);
    };

    const toggleMenu = () => {
        setConfirming(null);
        setMenuOpen((v) => !v);
    };

    const chooseFromMenu = (next) => {
        lastMenuActionAt.current = Date.now();
        setMenuOpen(false);
        next();
    };

    const startEditing = () => {
        setEditTitle(task.title);
        setEditDetails(task.details || '');
        setEditDueDate(toDateInputValue(task.due_date));
        setConfirming(null);
        setEditing(true);
    };

    const chooseEdit = () => chooseFromMenu(startEditing);
    const chooseCancel = () => chooseFromMenu(() => setConfirming('cancel'));
    const chooseDelete = () => chooseFromMenu(() => setConfirming('delete'));

    // Discarding throws the unsaved edits away — it never touches the status.
    const handleDiscardEdit = () => {
        refocusMore.current = true;
        setEditing(false);
    };

    const handleSaveEdit = () => {
        onEdit(task.id, {
            title: editTitle.trim(),
            details: editDetails,
            due_date: toDueDatePayload(editDueDate),
        });
        refocusMore.current = true;
        setEditing(false);
    };

    const keepTask = () => { refocusMore.current = true; setConfirming(null); };
    const confirmCancel = () => { setConfirming(null); onStatusChange(task.id, 'cancelled'); };
    const confirmDelete = () => onDelete(task.id);

    const quick = (status) => (e) => (onQuickStatus ? onQuickStatus(task.id, status, e) : onStatusChange(task.id, status));

    // Something on the card is open, or the menu has just been used.
    const busy = (e) => editing || menuOpen || confirming || (e && e.detail > 1)
        || Date.now() - lastMenuActionAt.current < CONFIRM_GUARD_MS;

    const handleCardClick = (e) => {
        if (busy(e)) return;
        if (canOpen) (onActivate || onOpenUnit)(task.stock_number, task);
    };

    // v2 (D10): a double-click keeps today's direct open.
    const handleCardDoubleClick = () => {
        if (editing || menuOpen || confirming || Date.now() - lastMenuActionAt.current < CONFIRM_GUARD_MS) return;
        if (canOpen) onOpenUnit(task.stock_number, task);
    };

    // Keys pressed on the card itself (not on a button or field inside it).
    const handleCardKeyDown = (e) => {
        if (e.key === 'Escape' && menuOpen) {
            e.stopPropagation();
            closeMenu(true);
            return;
        }
        if (e.target !== e.currentTarget || editing || menuOpen || confirming) return;
        if (e.altKey || e.ctrlKey || e.metaKey) return;
        const active = document.activeElement;
        if (isTypingTarget(active) || active?.closest?.(FOCUS_IN_OVERLAY_SELECTOR)) return;
        if (document.querySelector(OPEN_OVERLAY_SELECTOR)) return;
        if (e.key === 'Enter') {
            if (canOpen) onOpenUnit(task.stock_number, task);
            return;
        }
        if (ARROWS.includes(e.key)) {
            e.preventDefault();
            onNavigate?.(task.id, e.key);
            return;
        }
        if (!canEdit || !open) return;
        const key = e.key.toLowerCase();
        if (key === 's' && task.status === 'open') {
            e.preventDefault();
            onStatusChange(task.id, 'in_progress');
        } else if (key === 'c') {
            e.preventDefault();
            onStatusChange(task.id, 'completed');
        }
    };

    return {
        // state
        menuOpen, confirming, editing, editTitle, editDetails, editDueDate,
        setEditTitle, setEditDetails, setEditDueDate,
        // refs
        moreBtnRef, menuRef, titleInputRef, menuId,
        // derived
        typeLabel, type, open, due, canOpen, active: menuOpen || confirming !== null,
        // handlers
        toggleMenu, handleMenuKeyDown, handleQuickBlur, chooseEdit, chooseCancel, chooseDelete,
        handleDiscardEdit, handleSaveEdit, keepTask, confirmCancel, confirmDelete, quick,
        handleCardClick, handleCardDoubleClick, handleCardKeyDown,
    };
}
